// The dashboard as a runtime Program (runtime/effects.ts): its state, the
// messages that change it, and `update`, which gives the next state and
// the effects to run. Every piece of asynchronous work (a section's
// search, a batch of attention, the saved copy's read and write, the
// timers) runs in a lane of the Program's runner:
//
// - `search <i>`: section i's search. A load, refetch or Refresh starts
//   every section's again; a section's Try again its own. Either way the
//   run before is aborted (its waiting requests are dropped unsent) and
//   its answer never arrives.
// - `attention <n>`: one call's attention work. A load, refetch, Refresh
//   or open cancels them all: what a dropped run learns is not shown
//   (inputs it already read are still kept).
// - `recheck`: rows read again by ID before their attention is worked out
//   again (see below); `gap`: the cheap check of the rows shown when the
//   live stream opens.
// - `open`, `reviews`: reading the saved copy and the kept inputs that give
//   its rows their reviewers; `outside`, `save`: the native stack members
//   read by ID, and saving, once the sections are in.
// - `opening`, `notice`, `check again`, `recheck timer`, `copy age`,
//   `resume`: timers.
//
// So no update compares generations: an answer arrives only while its run
// is its lane's newest, and an account switch resets the Program (a new
// epoch: nothing of the old one is applied).
//
// What the user is told of a section is its `Remote` (runtime/remote.ts):
// loading, failed (with its retry), or its rows with their freshness (a
// saved copy's are stale as of when they were read; a refresh that failed
// says why) and gaps (matches that did not load). A retry is `Scheduled`
// only while the `resume` timer, armed through the runtime's `retry`
// effect, is pending: the dashboard reloads by itself when a rate limit
// lifts, and says so only then.
//
// An answer is as old as the reads behind it. Each known attention set
// carries when the work that read its row and inputs began
// (`KnownAttention.at`). When a pull request changes (the app wrote to it
// in any tab, the viewer opened it, a notice named it: the `changed`
// message, from the invalidation log through `AttentionInputs.onChanged`),
// what is known of it is forgotten, an answer whose reads began before the
// change is refused when it arrives (by the order the messages came in,
// `tick`, not by the clock), and the row is read again by ID, then
// its attention worked out from that row (`dirty`, the `recheck` lane). A
// saved copy's rows whose attention is unknown go the same way, so a turn
// is never worked out from a row older than the viewer's own write.
//
// A Refresh from GitHub is a property of the run (`Run.refresh`), or of an
// `open` reading the saved copy for it: it is over when that run is, on
// every path, and cannot be set with nothing running.
import { failureOf, type Failure } from '../../api/failure';
import { RateLimitError } from '../../api/gh';
import { DASH_ATTENTION_HITS, DASH_PAGE, type DashPr } from '../../api/queries';
import type { Notice } from '../../api/sse';
import { gapReason } from '../../logic/attentionGap';
import { hasMember } from '../../logic/attention';
import { byInvolvement } from '../../logic/involvement';
import { reviewSummary } from '../../logic/reviewSummary';
import { searchQuery, type Section } from '../../logic/sections';
import type { ReadTicket } from '../../runtime/cache';
import { after, cancel, retry, Scheduled, task, type Effect, type Next, type Outcome } from '../../runtime/effects';
import {
  failed,
  gaps,
  has,
  loading,
  manual,
  refreshFailed,
  settle,
  staleSince,
  type Freshness,
  type Gaps,
  type Remote,
  type Retry,
} from '../../runtime/remote';
import { keptInputs, prKeyOf, type AttentionInputs, type KeptInputs, type KeptResult } from '../attentionInputs';
import { SNAPSHOT_STALE_MS, type DashSnapshot, type SnapshotStore } from '../dashCache';
import {
  applyAttention,
  forgetAttention,
  noAttention,
  workOutAttention,
  type AttentionDeps,
  type AttentionResult,
  type KnownAttention,
} from './attention';
import { rowOf, type DashRow } from './rows';
import { cutOf, mergeHits, unloadedOf, type RowRead, type SearchPage, type SectionCut } from './search';
import { hydrate, snapshotOf, type Hydrated, type ShownPart } from './snapshot';
import { applyOutside, noOutside, planOutside, poolOf, type Outside, type OutsidePlan } from './stacks';

/** First wait before checking again kept attention inputs whose check failed; it doubles with each failure in a row, up to CHECK_RETRY_MAX_MS. */
export const CHECK_RETRY_MS = 30_000;
const CHECK_RETRY_MAX_MS = 5 * 60_000;
/**
 * Longest the view holds its loading placeholders back while `open` reads
 * the saved copy (`opening`), so saved rows appear in place of nothing
 * rather than push placeholders down.
 */
export const OPENING_WAIT_MS = 300;
/** How long opening the dashboard waits for the kept inputs, so saved rows show with their reviewers in one go. */
const REVIEWS_WAIT_MS = 150;
/** Notice kinds that change a dashboard row (title, state, reviews, threads, comments). */
const ROW_NOTICES = new Set(['pr', 'review', 'review_comment', 'thread', 'issue_comment']);
/** Delay before a notice-triggered reload, so a burst of notices costs one. */
const NOTICE_RELOAD_DELAY = 1500;
/**
 * Delay before rows that changed are read again by ID: longer than
 * NOTICE_RELOAD_DELAY, so a notice's own reload goes first and covers them.
 * It doubles each time a re-read's answer is refused in turn (a write still
 * landing), up to RECHECK_MAX_MS.
 */
export const RECHECK_DELAY_MS = 2000;
const RECHECK_MAX_MS = 60_000;
/** Minimum age of the last load before the window's focus reads again (as api/sse.ts `FOCUS_REFETCH_AFTER`). */
const FOCUS_REFETCH_AFTER_MS = 60_000;
/** Random delay added after a rate-limit pause, so tabs do not reload at once. */
const RESUME_JITTER_MS = 2000;

/** What a section shows when it has rows. */
export interface SectionRows {
  rows: DashRow[];
  /** A warning about some of its rows (an attention section's rows whose attention could not be told), or null. */
  note: string | null;
  /**
   * Set when a query matched more pull requests than were read: `checked`
   * (the most recently updated hits loaded, summed over the section's
   * queries) of `total` (GitHub's match count, summed likewise).
   */
  cut: SectionCut | null;
  /**
   * An attention section's rows held back while their attention is worked
   * out again (a saved copy's rows, or rows that changed since they were
   * read): how many; 0 for none. They are never shown as waiting on the
   * viewer meanwhile.
   */
  checking: number;
}

/** A section's load state: loading, failed, or its rows with their freshness and gaps. */
export type SectionLoad = Remote<SectionRows>;

/** Matches a section read but could not load, and the failure that cost them. */
export interface SectionUnloaded {
  count: number;
  failure: Failure;
}

/** A section's search under way: searching, or waiting for its rows' attention. */
type Flow =
  | { readonly at: 'idle' }
  | { readonly at: 'searching'; readonly fresh: boolean; readonly reload: Reload | null }
  | {
      readonly at: 'attention';
      readonly fresh: boolean;
      readonly reload: Reload | null;
      /** When its search answered: when its rows were read. */
      readonly read: number;
      readonly prs: DashPr[];
      readonly cut: SectionCut | null;
      readonly unloaded: SectionUnloaded | null;
    };

/**
 * When work that reads rows began: among the changes heard
 * (`DashState.tick`), and the ticket taken then, under which what it
 * learns is saved (null where nothing is saved).
 */
interface Began {
  readonly tick: number;
  readonly ticket: ReadTicket | null;
}

/** A section's own Try again. */
type Reload = Began;

export interface Slot {
  readonly shown: SectionLoad;
  /** Its pull requests from its latest search, or the saved copy; undefined while it has none. */
  readonly loaded: DashPr[] | undefined;
  readonly cut: SectionCut | null;
  readonly unloaded: SectionUnloaded | null;
  /** When the rows it shows were read from GitHub. */
  readonly readAt: number | undefined;
  readonly flow: Flow;
}

/** A load: every section's search, then their rows' attention. */
interface Run extends Began {
  /** Names the run for the checks of kept inputs sent together. */
  readonly key: string;
  /** Every row's attention inputs are read from GitHub, none kept used. */
  readonly fresh: boolean;
  /** It is a Refresh from GitHub (or took one over): the Refresh is over when it is. */
  readonly refresh: boolean;
  /** Pull request numbers its searches found, by lowercased `owner/name`. */
  readonly found: ReadonlyMap<string, ReadonlySet<number>>;
}

/** Rows read again by ID, then their attention (see the file comment). */
interface Recheck extends Began {
  readonly phase: 'rows' | 'attention';
  /** The rows being read again, then those whose attention is being worked out. */
  readonly ids: ReadonlySet<string>;
}

/** What follows the sections: the native stack members no section loaded, then saving. */
interface Tail {
  readonly phase: 'outside' | 'saving';
  /** Read every member again (a load), or only those not read yet. */
  readonly all: boolean;
  /** Save always, or only when the members changed. */
  readonly save: 'always' | 'changed';
  /** The work whose sections it saves: the oldest, when several ended together. */
  readonly ticket: ReadTicket | null;
  readonly plan: OutsidePlan | null;
}

export interface DashState {
  readonly sections: Section[];
  readonly slots: readonly Slot[];
  readonly known: KnownAttention;
  /**
   * Every row loaded by any section's latest successful search, once each
   * (its most recently updated copy), with its attention as known: the
   * rows an attention section leaves out too. Stacks are found among them.
   * The native stack members no section loaded, read by ID, are here too,
   * marked `outside`, their attention unknown.
   */
  readonly pool: DashRow[];
  readonly outside: Outside;
  /** Whether `open` is still reading the saved copy (at most OPENING_WAIT_MS). */
  readonly opening: boolean;
  /** The saved copy being read by `open`, and how it goes on from it; null when none is. */
  readonly reading: { readonly revalidate: boolean; readonly refresh: boolean } | null;
  /** A refetch asked for during a Refresh: run once after it. */
  readonly queued: boolean;
  readonly run: Run | null;
  readonly recheck: Recheck | null;
  /** Rows shown whose pull request changed since they were read (or a saved copy's with no attention): read again by ID. */
  readonly dirty: ReadonlySet<string>;
  /** Counts the changes heard: orders them against the starts of work that reads rows. */
  readonly tick: number;
  /** The latest change heard of each pull request (`prKeyOf`), as its `tick`, since the latest load began. */
  readonly changes: ReadonlyMap<string, number>;
  /** Whether the dirty rows are due to be read again (their delay has passed). */
  readonly recheckDue: boolean;
  /** Re-reads in a row whose answers were refused; the delay before the next doubles with each. */
  readonly recheckRefused: number;
  readonly tail: Tail | null;
  /** Rows whose attention this run works out or has: worked out once per run, whichever sections list them. */
  readonly attn: ReadonlyMap<string, 'running' | 'done'>;
  readonly attnLanes: readonly string[];
  /** Counts runs and lanes, to name them. */
  readonly seq: number;
  readonly lastLoadAt: number;
  /** Pull request numbers shown, by lowercased `owner/name` (the live topics). */
  readonly shown: ReadonlyMap<string, ReadonlySet<number>>;
  /** The reload armed for when a rate limit lifts. */
  readonly resume: Scheduled | 'arming' | null;
  /**
   * The live stream opened while a load was under way: its reads may have
   * begun before a notice it never heard, so its rows are checked once it
   * is done.
   */
  readonly missed: boolean;
  /** Failed checks of kept inputs in a row; the wait before the next doubles with each. */
  readonly checkFailures: number;
  readonly checkArmed: boolean;
}

/** What the saved copy came to on opening. */
interface OpenRead {
  saved: DashSnapshot | null;
  /** The kept inputs for its rows, when they came within REVIEWS_WAIT_MS. */
  early: Map<string, KeptInputs> | null;
  /** Them, when they did not. */
  later: Promise<Map<string, KeptInputs>> | null;
}

/** Why the live handler was called without a notice (api/sse.ts `Trigger`). */
type Trigger = 'open' | 'reconnect' | 'resync' | 'focus';

export type Msg =
  | { t: 'open'; sections: Section[]; revalidate: boolean }
  | { t: 'opened' }
  | { t: 'saved-read'; o: Outcome<OpenRead> }
  | { t: 'reviews'; prs: DashPr[]; o: Outcome<Map<string, KeptInputs>> }
  | { t: 'load'; sections: Section[]; fresh: boolean }
  | { t: 'refetch' }
  | { t: 'refresh' }
  | { t: 'reload'; si: number }
  | { t: 'searched'; si: number; o: Outcome<SearchPage[]> }
  | { t: 'rows'; si: number; rows: DashPr[] }
  | { t: 'attention'; lane: string; prs: DashPr[]; began: Began; o: Outcome<AttentionResult> }
  | { t: 'changed'; key: string }
  | { t: 'recheck' }
  | { t: 'rechecked'; prs: DashPr[]; o: Outcome<RowRead[]> }
  | { t: 'gap'; prs: DashPr[]; o: Outcome<KeptResult> }
  | { t: 'outside'; o: Outcome<RowRead[]> }
  | { t: 'saved' }
  | { t: 'notice'; topic: string; notice: Notice | null }
  | { t: 'done' }
  | { t: 'resume-armed'; s: Scheduled }
  | { t: 'resume-fire'; s: Scheduled }
  | { t: 'resume-lapsed'; s: Scheduled }
  | { t: 'check-again' };

/** What `update` works with: the store's capabilities and the page's helpers. Besides them, it reads nothing but its state and message. */
export interface ProgramDeps {
  me: () => string;
  search: (q: string, first: number, opts: { signal: AbortSignal; onRows: (rows: DashPr[]) => void }) => Promise<SearchPage>;
  rows: ((ids: string[]) => Promise<RowRead[]>) | null;
  snapshot: SnapshotStore | null;
  kept: AttentionInputs;
  attention: AttentionDeps;
  /** Sends a message to the Program (a search handing over rows as they load). */
  send: (m: Msg) => void;
  /** Until when GitHub calls are paused (epoch ms), or null. */
  pausedUntil: () => number | null;
  now: () => number;
  random: () => number;
}

export function initialState(): DashState {
  return {
    sections: [],
    slots: [],
    known: noAttention,
    pool: [],
    outside: noOutside,
    opening: false,
    reading: null,
    queued: false,
    run: null,
    recheck: null,
    dirty: new Set(),
    tick: 0,
    changes: new Map(),
    recheckDue: false,
    recheckRefused: 0,
    tail: null,
    attn: new Map(),
    attnLanes: [],
    seq: 0,
    lastLoadAt: 0,
    shown: new Map(),
    resume: null,
    missed: false,
    checkFailures: 0,
    checkArmed: false,
  };
}

const idle: Flow = { at: 'idle' };
const emptySlot: Slot = { shown: loading, loaded: undefined, cut: null, unloaded: null, readAt: undefined, flow: idle };

/** Whether the store has work under way (a load, a section's Try again, opening, a re-read of changed rows, or what follows them). */
export function busy(s: DashState): boolean {
  return s.reading !== null || s.run !== null || s.recheck !== null || s.tail !== null || s.slots.some((x) => x.flow.at !== 'idle');
}

/** Whether a Refresh from GitHub is running: a run that is one, or an `open` reading the saved copy for one. */
export function refreshingOf(s: DashState): boolean {
  return s.run?.refresh === true || s.reading?.refresh === true;
}

/** Whether GitHub is being asked for newer rows (a load, a Refresh, or a re-read of changed rows). */
export function checkingOf(s: DashState): boolean {
  return s.run !== null || s.recheck !== null;
}

const repoOf = (p: DashPr) => p.repository.nameWithOwner.toLowerCase();
const keyOf = (p: DashPr) => prKeyOf(p.repository.nameWithOwner, p.number);

/** `shown` with `prs` added. */
function withShown(shown: ReadonlyMap<string, ReadonlySet<number>>, prs: DashPr[]): Map<string, Set<number>> {
  const out = new Map([...shown].map(([k, v]) => [k, new Set(v)]));
  for (const p of prs) {
    const set = out.get(repoOf(p)) ?? new Set<number>();
    set.add(p.number);
    out.set(repoOf(p), set);
  }
  return out;
}

/** The retry a failure is shown with: the armed reload for a rate limit, while it is pending; else the user's. */
function retryFor(s: DashState, f: Failure): Retry {
  return f.cause === 'rate_limited' && s.resume instanceof Scheduled && s.resume.pending ? s.resume : manual;
}

/** `r` with each rate-limited failure's manual retry taken over by `sc`, the reload armed for the limit's end. */
function adopt<T>(r: Remote<T>, sc: Scheduled): Remote<T> {
  const take = (reason: Failure, x: Retry): Retry => (x.kind === 'manual' && reason.cause === 'rate_limited' ? sc : x);
  if (r.tag === 'Failed') return failed(r.reason, take(r.reason, r.retry));
  if (r.tag !== 'Has') return r;
  const f = r.freshness;
  const freshness: Freshness =
    f.kind === 'stale' && f.refresh.kind === 'failed' ? staleSince(f.since, refreshFailed(f.refresh.reason, take(f.refresh.reason, f.refresh.retry))) : f;
  const g: Gaps | null = r.gaps && { ...r.gaps, retry: take(r.gaps.reason, r.gaps.retry) };
  return has(r.value, freshness, g);
}

const withSlot = (s: DashState, si: number, slot: Partial<Slot>): DashState => ({
  ...s,
  slots: s.slots.map((x, i) => (i === si ? { ...x, ...slot } : x)),
});

const withPool = (s: DashState): DashState => ({ ...s, pool: poolOf(s.slots.map((x) => x.loaded), s.outside.prs, s.known) });

/** Every row the sections hold, once each. */
function loadedRows(s: DashState): Map<string, DashPr> {
  const out = new Map<string, DashPr>();
  for (const slot of s.slots) for (const p of slot.loaded ?? []) if (!out.has(p.id)) out.set(p.id, p);
  return out;
}

/** How a section's rows are published (see `publish`). */
interface Publish {
  /** The rows were just read from GitHub: current. Otherwise their freshness stays (a saved copy's: as of when read). */
  fresh: boolean;
  /**
   * A saved copy's rows: shown at once; in an attention section, those
   * whose attention is still being worked out are held back and counted.
   * Otherwise an attention section waits until every row's attention is
   * known or has failed, holding back only rows that changed since they
   * were read (`dirty`).
   */
  saved: boolean;
}

/**
 * Publish section `si`'s rows. An attention section keeps the rows waiting
 * on the viewer plus those whose attention is unknown (they may belong),
 * with a note naming how many are unknown. Rows whose review is asked of
 * the viewer and that are assigned to them come first (`byInvolvement`).
 */
function publish(s: DashState, deps: ProgramDeps, si: number, prs: DashPr[], cut: SectionCut | null, unloaded: SectionUnloaded | null, how: Publish): DashState {
  const section = s.sections[si];
  const slot = s.slots[si];
  let next = withPool(withSlot(s, si, { loaded: prs, cut, unloaded }));
  const rows = byInvolvement(
    prs.map((pr) => rowOf(pr, next.known)),
    deps.me(),
  );
  const was = slot.shown;
  const freshness: Freshness = how.fresh ? { kind: 'current' } : was.tag === 'Has' ? was.freshness : staleSince(slot.readAt ?? deps.now());
  const g = unloaded ? gaps(unloaded.count, unloaded.failure, retryFor(s, unloaded.failure)) : null;
  if (!section.attention) return withSlot(next, si, { shown: has({ rows, note: null, cut, checking: 0 }, freshness, g) });
  const pending = rows.filter((r) => r.attention === null && r.attentionError === null);
  if (!how.saved && pending.some((r) => !s.dirty.has(r.pr.id))) return next; // its rows wait for their attention
  const me = deps.me();
  const unknown = rows.flatMap((r) => (r.attentionError === null ? [] : [r.attentionError]));
  const n = unknown.length;
  next = withSlot(next, si, {
    shown: has(
      {
        // Rows still being worked out are held back: never shown as waiting on the viewer.
        rows: rows.filter((r) => r.attentionError !== null || (r.attention !== null && hasMember(r.attention, me))),
        note: n ? `Couldn't check whether ${n} pull request${n === 1 ? ' is' : 's are'} waiting on you (${gapReason(unknown[0])})` : null,
        cut,
        checking: pending.length,
      },
      freshness,
      g,
    ),
  });
  return next;
}

/**
 * Publish again every section that shows rows and has no search under way
 * (one that has publishes its own), from what is known now; their
 * freshness stays.
 */
function republish(s: DashState, deps: ProgramDeps): DashState {
  let next = s;
  s.slots.forEach((slot, si) => {
    if (slot.shown.tag === 'Has' && slot.flow.at === 'idle')
      next = publish(next, deps, si, slot.loaded ?? [], slot.cut, slot.unloaded, { fresh: false, saved: true });
  });
  return next;
}

type Fx = Effect<Msg>[];

/** The update function, with `deps` (see the file comment). */
export function makeUpdate(deps: ProgramDeps): (s: DashState, m: Msg) => Next<DashState, Msg> {
  const begin = () => deps.snapshot?.begin() ?? null;
  const paused = () => (deps.pausedUntil() ?? -Infinity) > deps.now();
  /** Whether `trigger` reads again now: never while GitHub calls are paused; the window's focus only for data old enough. */
  const mayRefetch = (trigger: Trigger | 'notice', s: DashState) =>
    !paused() && (trigger !== 'focus' || deps.now() - s.lastLoadAt >= FOCUS_REFETCH_AFTER_MS);
  /** Saves run one after another: each writes what changed against what the one before left. */
  let saving: Promise<unknown> = Promise.resolve();

  /** The section's search, in its lane: rows are handed over as they load while the lane is current. */
  const searchTask = (s: DashState, si: number): Effect<Msg> => {
    const section = s.sections[si];
    const first = section.attention ? DASH_ATTENTION_HITS : DASH_PAGE;
    return task(
      `search ${si}`,
      (signal) =>
        Promise.all(
          section.queries.map((q) =>
            deps.search(searchQuery(q), first, {
              signal,
              // Attention for rows as they arrive, beside the search's remaining requests.
              onRows: (rows) => {
                if (!signal.aborted) deps.send({ t: 'rows', si, rows });
              },
            }),
          ),
        ),
      (o) => ({ t: 'searched', si, o }),
    );
  };

  /**
   * Work out the attention of those of `prs` this run has not (see
   * `DashState.attn`), from rows read by work that `began` then.
   */
  const startAttention = (s: DashState, prs: DashPr[], fresh: boolean, began: Began, fx: Fx): DashState => {
    const todo = prs.filter((p, i) => !s.attn.has(p.id) && prs.findIndex((q) => q.id === p.id) === i);
    if (!todo.length) return s;
    const lane = `attention ${s.seq}`;
    const run = s.run?.key ?? `run ${s.seq}`;
    fx.push(
      task(lane, (signal) => workOutAttention(todo, deps.attention, { fresh, run, signal }), (o) => ({ t: 'attention', lane, prs: todo, began: { tick: began.tick, ticket: began.ticket }, o })),
    );
    const attn = new Map(s.attn);
    for (const p of todo) attn.set(p.id, 'running');
    return { ...s, attn, attnLanes: [...s.attnLanes, lane], seq: s.seq + 1 };
  };

  /** Arm the reload for when the rate limit lifts (at `until` at the latest it said), unless one is armed. */
  const armResume = (s: DashState, fx: Fx, until: number | null = null): DashState => {
    if (s.resume !== null) return s;
    const now = deps.now();
    const end = Math.max(deps.pausedUntil() ?? -Infinity, until ?? -Infinity);
    const at = end > now ? end + deps.random() * RESUME_JITTER_MS : now;
    fx.push(
      retry<Msg>('resume', at, {
        armed: (sc) => ({ t: 'resume-armed', s: sc }),
        fire: (sc) => ({ t: 'resume-fire', s: sc }),
        lapsed: (sc) => ({ t: 'resume-lapsed', s: sc }),
      }),
    );
    return { ...s, resume: 'arming' };
  };

  /** Check again later, with backoff, what a failed check could not confirm. */
  const armCheckAgain = (s: DashState, fx: Fx): DashState => {
    if (s.checkArmed) return s;
    const wait = Math.min(CHECK_RETRY_MS * 2 ** s.checkFailures, CHECK_RETRY_MAX_MS);
    fx.push(after('check again', wait, { t: 'check-again' }));
    return { ...s, checkArmed: true, checkFailures: s.checkFailures + 1 };
  };

  /**
   * Start a load of `sections`: every section searched again, in its lane.
   * `keep`: ready sections keep their rows on screen until their new
   * results arrive. `fresh` (Try again, Refresh): every row's attention
   * inputs are read from GitHub, none kept used. `refresh`: it is a
   * Refresh; a load that replaces a Refresh takes it over, so the Refresh
   * ends when a run completes, whichever. What runs before is dropped:
   * searches, attention, a re-read of changed rows (its rows are searched
   * for again), the saved copy's read, the stack members' read (a save
   * under way finishes).
   */
  const startRun = (s: DashState, sections: Section[], keep: boolean, fresh: boolean, fx: Fx, refresh = false): DashState => {
    refresh ||= refreshingOf(s);
    fresh ||= refresh;
    for (let i = sections.length; i < s.slots.length; i++) fx.push(cancel(`search ${i}`));
    for (const lane of s.attnLanes) fx.push(cancel(lane));
    fx.push(cancel('open'), cancel('outside'), cancel('opening'), cancel('recheck'), cancel('recheck timer'), cancel('gap'), cancel('copy age'));
    const slots = sections.map((_, i): Slot => {
      const was = s.slots[i];
      const kept = keep && was?.shown.tag === 'Has' ? was : { ...emptySlot, loaded: keep ? was?.loaded : undefined };
      return { ...kept, flow: { at: 'searching', fresh, reload: null } };
    });
    let next: DashState = {
      ...s,
      sections,
      slots,
      opening: false,
      reading: null,
      recheck: null,
      dirty: new Set(),
      // Its reads begin after every change heard so far.
      changes: new Map(),
      recheckDue: false,
      attn: new Map(),
      attnLanes: [],
      seq: s.seq + 1,
      run: { key: `run ${s.seq}`, fresh, refresh, tick: s.tick, ticket: begin(), found: new Map() },
      tail: s.tail?.phase === 'saving' ? s.tail : null,
    };
    if (!keep) next = withPool(next);
    sections.forEach((_, si) => fx.push(searchTask(next, si)));
    return next;
  };

  /** A refetch: the current sections again, keeping their rows; after the Refresh running, once. */
  const refetch = (s: DashState, fx: Fx): DashState => (refreshingOf(s) ? { ...s, queued: true } : startRun(s, s.sections, true, false, fx));

  /**
   * Begin what follows the sections (see `Tail`). One already waiting is
   * taken over: what either would read or save is read and saved.
   */
  const startTail = (s: DashState, t: Omit<Tail, 'phase' | 'plan'>, fx: Fx): DashState => {
    fx.push(cancel('outside'));
    const was = s.tail;
    const older = (a: ReadTicket | null, b: ReadTicket | null) => (a && b ? (a.basis <= b.basis ? a : b) : (a ?? b));
    const merged: Omit<Tail, 'phase' | 'plan'> = was
      ? { all: t.all || (was.phase === 'outside' && was.all), save: t.save === 'always' || was.save === 'always' ? 'always' : 'changed', ticket: older(t.ticket, was.ticket) }
      : t;
    if (!deps.rows) return toSaving(s, { ...merged, phase: 'outside', plan: null }, false, fx);
    const plan = planOutside(s.slots.map((x) => x.loaded), s.outside, merged.all);
    const tail: Tail = { ...merged, phase: 'outside', plan };
    if (!plan.todo.length) return outsideRead(s, tail, [], fx);
    const read = deps.rows;
    fx.push(task('outside', () => read(plan.todo.map((m) => m.id)), (o) => ({ t: 'outside', o })));
    return { ...s, tail };
  };

  /** The members read (`got`; none when the read failed): applied, then saving. */
  const outsideRead = (s: DashState, tail: Tail, got: RowRead[] | null, fx: Fx): DashState => {
    const outside = got && tail.plan ? applyOutside(s.outside, tail.plan, got, tail.all) : null;
    const next = outside ? withPool({ ...s, outside }) : s;
    return toSaving(next, tail, outside !== null, fx);
  };

  /** Save the dashboard as shown (when `tail` says to). */
  const toSaving = (s: DashState, tail: Tail, changed: boolean, fx: Fx): DashState => {
    if (tail.save === 'changed' && !changed) return { ...s, tail: null };
    const store = deps.snapshot;
    const login = deps.me();
    const kept = deps.kept;
    const ticket = tail.ticket;
    const parts: ShownPart[] = [];
    s.sections.forEach((section, si) => {
      const slot = s.slots[si];
      if (slot.shown.tag !== 'Has' || slot.readAt === undefined) return;
      parts.push({ section, prs: slot.loaded ?? [], at: slot.readAt, cut: slot.cut, unloaded: slot.unloaded?.count ?? 0 });
    });
    const snap = store && login && ticket ? snapshotOf(login, parts, s.known, s.outside, (k, at) => kept.changedSince(k, at), deps.now()) : null;
    fx.push(
      task(
        'save',
        () => {
          const write = async () => {
            void kept.save();
            if (store && snap && ticket) await store.write(snap, ticket);
          };
          const done = saving.then(write, write);
          saving = done.catch(() => {});
          return done;
        },
        () => ({ t: 'saved' }),
      ),
    );
    return { ...s, tail: { ...tail, phase: 'saving' } };
  };

  /** Read again later the rows that changed: after RECHECK_DELAY_MS, longer after re-reads whose answers were refused. */
  const armRecheck = (s: DashState, fx: Fx): DashState => {
    fx.push(after('recheck timer', Math.min(RECHECK_DELAY_MS * 2 ** s.recheckRefused, RECHECK_MAX_MS), { t: 'recheck' }));
    return s;
  };

  /**
   * Read the dirty rows again by ID, once they are due and nothing else is
   * reading rows (a load or a section's Try again reads them itself, or
   * goes first). Without rows by ID, the sections are searched again.
   */
  const maybeRecheck = (s: DashState, fx: Fx): DashState => {
    if (!s.dirty.size || !s.recheckDue || s.recheck || s.reading || s.run || s.slots.some((x) => x.flow.at !== 'idle')) return s;
    const loaded = loadedRows(s);
    const prs = [...s.dirty].flatMap((id) => loaded.get(id) ?? []);
    if (!prs.length) return { ...s, dirty: new Set(), recheckDue: false };
    // While GitHub calls are paused, the reload at the pause's end reads them.
    if (paused()) return armResume(s, fx);
    const read = deps.rows;
    if (!read) return refetch({ ...s, recheckDue: false }, fx);
    const batches: DashPr[][] = [];
    for (let i = 0; i < prs.length; i += DASH_PAGE) batches.push(prs.slice(i, i + DASH_PAGE));
    fx.push(
      task('recheck', async () => (await Promise.all(batches.map((b) => read(b.map((p) => p.id))))).flat(), (o) => ({ t: 'rechecked', prs, o })),
    );
    return {
      ...s,
      recheckDue: false,
      dirty: new Set(prs.map((p) => p.id)),
      recheck: { phase: 'rows', ids: new Set(prs.map((p) => p.id)), tick: s.tick, ticket: begin() },
    };
  };

  /** Mark these shown rows changed: what is known of them is forgotten, they are held back, and they are read again. */
  const markDirty = (s: DashState, ids: string[], due: boolean, fx: Fx): DashState => {
    if (!ids.length) return s;
    const next = republish(withPool({ ...s, known: forgetAttention(s.known, ids), dirty: new Set([...s.dirty, ...ids]), recheckDue: s.recheckDue || due }), deps);
    return due ? maybeRecheck(next, fx) : armRecheck(next, fx);
  };

  /** Section `si`'s rows' attention is in: publish them, and end its search. */
  const finishSection = (s: DashState, si: number, fx: Fx): DashState => {
    const flow = s.slots[si].flow;
    if (flow.at !== 'attention') return s;
    let next = publish(s, deps, si, flow.prs, flow.cut, flow.unloaded, { fresh: true, saved: false });
    next = withSlot(next, si, { flow: idle, ...(next.slots[si].shown.tag === 'Has' ? { readAt: flow.read } : {}) });
    if (flow.reload) {
      next = { ...next, shown: withShown(next.shown, flow.prs) };
      next = startTail(next, { all: false, save: 'always', ticket: flow.reload.ticket }, fx);
    }
    return next;
  };

  /** Move on whatever waited for attention: sections, a re-read, then the run; then what waited for those. */
  const progress = (s: DashState, fx: Fx): DashState => {
    let next = s;
    const settled = (ids: Iterable<string>) => [...ids].every((id) => next.attn.get(id) !== 'running');
    next.slots.forEach((slot, si) => {
      if (slot.flow.at === 'attention' && settled(slot.flow.prs.map((p) => p.id))) next = finishSection(next, si, fx);
    });
    const recheck = next.recheck;
    if (recheck?.phase === 'attention' && settled(recheck.ids)) {
      const refused = [...recheck.ids].some((id) => next.dirty.has(id));
      next = republish({ ...next, recheck: null, recheckRefused: refused ? next.recheckRefused + 1 : 0 }, deps);
      next = startTail(next, { all: false, save: 'always', ticket: recheck.ticket }, fx);
    }
    const run = next.run;
    if (run && next.slots.every((x) => x.flow.at === 'idle')) {
      const queued = run.refresh && next.queued;
      next = { ...next, run: null, queued: run.refresh ? false : next.queued, lastLoadAt: deps.now(), shown: run.found };
      next = startTail(next, { all: true, save: 'always', ticket: run.ticket }, fx);
      // A refetch that waited for the Refresh reads everything again: it covers what the stream's opening missed.
      if (queued) return startRun({ ...next, missed: false }, next.sections, true, false, fx);
      if (next.missed) next = gapCheck({ ...next, missed: false }, fx);
    }
    return maybeRecheck(next, fx);
  };

  /**
   * Check, with one cheap request per CHECK_BATCH rows, whether the rows a
   * load read changed since (their own `updatedAt`, their threads' resolved
   * states, against the inputs kept from that load): those that did are
   * read again by ID.
   */
  const gapCheck = (s: DashState, fx: Fx): DashState => {
    const prs = [...loadedRows(s).values()];
    if (!prs.length) return s;
    fx.push(task('gap', (signal) => keptInputs(prs, deps.kept, { signal }), (o) => ({ t: 'gap', prs, o })));
    return s;
  };

  /**
   * The live stream opened: a notice about a row shown that came after the
   * row was read and before the stream went live was never heard. Rows a
   * load read are checked for a change since (`gapCheck`; once a load under
   * way is done). A saved copy's rows are read again once the copy is
   * SNAPSHOT_STALE_MS old: what changed before then is the copy's accepted
   * staleness. With no row shown (only the user topic), nothing could have
   * been missed.
   */
  const streamOpened = (s: DashState, fx: Fx): DashState => {
    if (!s.shown.size) return s;
    if (s.run || s.slots.some((x) => x.flow.at !== 'idle')) return { ...s, missed: true };
    const loaded = s.slots.some((x) => x.shown.tag === 'Has' && x.shown.freshness.kind === 'current');
    const copy = s.slots.flatMap((x) =>
      x.shown.tag === 'Has' && x.shown.freshness.kind === 'stale' && x.shown.freshness.refresh.kind === 'idle' && x.readAt !== undefined ? [x.readAt] : [],
    );
    if (!loaded && copy.length) {
      const due = Math.min(...copy) + SNAPSHOT_STALE_MS - deps.now();
      if (due > 0) {
        fx.push(after('copy age', due, { t: 'refetch' }));
        return s;
      }
      return mayRefetch('open', s) ? refetch(s, fx) : armResume(s, fx);
    }
    return paused() ? armResume(s, fx) : gapCheck(s, fx);
  };

  /**
   * Give these rows of the saved copy their reviewers from the kept inputs,
   * unless they have them (a run read them), and show them again, unless a
   * load is under way (it shows them itself).
   */
  const fillReviews = (s: DashState, prs: DashPr[], inputs: Map<string, KeptInputs>): DashState => {
    const reviews = new Map(s.known.reviews);
    let filled = false;
    for (const p of prs) {
      const input = inputs.get(p.id);
      if (input && s.known.attention.has(p.id) && !reviews.has(p.id)) {
        reviews.set(p.id, reviewSummary(p, input.inputs));
        filled = true;
      }
    }
    if (!filled) return s;
    const next = withPool({ ...s, known: { ...s.known, reviews } });
    return next.run ? next : republish(next, deps);
  };

  /** Show the saved copy `h` holds, then check it as its age says (see DashboardStore.open). */
  const shownSaved = (s: DashState, h: Hydrated, read: OpenRead, how: { revalidate: boolean; refresh: boolean }, fx: Fx): DashState => {
    let next: DashState = {
      ...s,
      known: h.known,
      outside: h.outside,
      slots: s.slots.map((slot, i) => {
        const part = h.parts[i];
        return part ? { ...slot, loaded: part.prs, cut: part.cut, unloaded: null, readAt: part.at } : slot;
      }),
    };
    next = withPool(next);
    h.parts.forEach((part, si) => {
      if (part) next = publish(next, deps, si, part.prs, part.cut, null, { fresh: false, saved: true });
    });
    const times = h.parts.flatMap((p) => (p ? [p.at] : []));
    next = { ...next, lastLoadAt: Math.min(...times), shown: withShown(new Map(), h.parts.flatMap((p) => p?.prs ?? [])) };
    // Filled in the same update as the rows are shown, the page never paints them without.
    if (read.early) next = fillReviews(next, h.shown, read.early);
    else if (read.later) {
      const later = read.later;
      fx.push(task('reviews', () => later, (o) => ({ t: 'reviews', prs: h.shown, o })));
    }
    // A Refresh under way reads everything afresh, whatever the copy's age.
    if (how.refresh) return startRun(next, next.sections, true, true, fx, true);
    const now = deps.now();
    const oldest = Math.min(...next.sections.map((_, si) => h.parts[si]?.at ?? -Infinity));
    if (how.revalidate || h.incomplete || !(now - oldest < SNAPSHOT_STALE_MS && now >= oldest)) return refetch(next, fx);
    // Rows whose attention the copy does not vouch for: their rows are as old, so both are read again.
    if (h.unknown.length) return markDirty(next, h.unknown.map((p) => p.id), true, fx);
    return startTail(next, { all: false, save: 'changed', ticket: begin() }, fx);
  };

  /** The saved copy and the kept inputs for its rows (see `OpenRead`). */
  const readSaved = async (): Promise<OpenRead> => {
    const store = deps.snapshot;
    let saved: DashSnapshot | null = null;
    try {
      saved = (await store?.read()) ?? null;
    } catch {
      saved = null;
    }
    const me = deps.me();
    if (saved && saved.login.toLowerCase() !== me.toLowerCase()) saved = null;
    if (!saved) return { saved: null, early: null, later: null };
    const inputs = deps.kept.candidates(saved.sections.flatMap((x) => x.prs)).catch(() => new Map<string, KeptInputs>());
    const early = await Promise.race([inputs, new Promise<null>((r) => setTimeout(() => r(null), REVIEWS_WAIT_MS))]);
    return { saved, early, later: early ? null : inputs };
  };

  return (s: DashState, m: Msg): Next<DashState, Msg> => {
    const fx: Fx = [];
    const done = (next: DashState): Next<DashState, Msg> => [next, fx];
    switch (m.t) {
      case 'open': {
        for (let i = 0; i < s.slots.length; i++) fx.push(cancel(`search ${i}`));
        for (const lane of s.attnLanes) fx.push(cancel(lane));
        fx.push(cancel('reviews'), cancel('outside'), cancel('recheck'), cancel('recheck timer'), cancel('gap'), cancel('copy age'));
        fx.push(after('opening', OPENING_WAIT_MS, { t: 'opened' }), task('open', readSaved, (o) => ({ t: 'saved-read', o })));
        const next: DashState = {
          ...s,
          sections: m.sections,
          slots: m.sections.map(() => emptySlot),
          opening: true,
          // A Refresh under way goes on in the run this open leads to.
          reading: { revalidate: m.revalidate, refresh: refreshingOf(s) },
          run: null,
          recheck: null,
          dirty: new Set(),
          recheckDue: false,
          tail: s.tail?.phase === 'saving' ? s.tail : null,
          attn: new Map(),
          attnLanes: [],
        };
        return done(withPool(next));
      }
      case 'opened':
        return done({ ...s, opening: false });
      case 'saved-read': {
        const how = s.reading;
        if (!how) return done(s);
        fx.push(cancel('opening'));
        const read = m.o.ok ? m.o.value : { saved: null, early: null, later: null };
        const opened: DashState = { ...s, opening: false, reading: null };
        const h = read.saved ? hydrate(s.sections, read.saved, s.known, (k, at) => deps.kept.changedSince(k, at), deps.now()) : null;
        if (!h) return done(startRun(opened, s.sections, false, how.refresh, fx, how.refresh));
        return done(shownSaved(opened, h, read, how, fx));
      }
      case 'reviews':
        return done(m.o.ok ? fillReviews(s, m.prs, m.o.value) : s);
      case 'load':
        return done(startRun({ ...s, slots: [] }, m.sections, false, m.fresh, fx));
      case 'refetch':
        return done(refetch(s, fx));
      case 'refresh':
        return done(startRun(s, s.sections, true, true, fx, true));
      case 'reload': {
        const slot = s.slots[m.si];
        if (!s.sections[m.si] || !slot) return done(s);
        const attn = new Map(s.attn);
        for (const p of slot.loaded ?? []) if (s.known.errors.has(p.id)) attn.delete(p.id);
        // A section showing rows keeps them on screen until its new results arrive.
        const next = withSlot({ ...s, attn }, m.si, {
          ...(slot.shown.tag === 'Has' ? {} : { shown: loading }),
          flow: { at: 'searching', fresh: true, reload: { tick: s.tick, ticket: begin() } },
        });
        fx.push(searchTask(next, m.si));
        return done(next);
      }
      case 'rows': {
        const flow = s.slots[m.si]?.flow;
        if (flow?.at !== 'searching') return done(s);
        return done(startAttention(s, m.rows, flow.fresh, flow.reload ?? s.run ?? { tick: s.tick, ticket: begin() }, fx));
      }
      case 'searched': {
        const slot = s.slots[m.si];
        const flow = slot?.flow;
        if (flow?.at !== 'searching') return done(s);
        if (!m.o.ok) {
          const e = m.o.error;
          const f = failureOf(e);
          let next = s;
          if (e instanceof RateLimitError) next = armResume(next, fx, e.limit.until);
          const was = slot.shown;
          // Rows already on screen stay, marked as from an earlier read; else its earlier rows would shadow fresher copies.
          next =
            was.tag === 'Has'
              ? withSlot(next, m.si, { shown: has(was.value, staleSince(slot.readAt ?? deps.now(), refreshFailed(f, retryFor(next, f))), was.gaps), flow: idle })
              : withPool(withSlot(next, m.si, { shown: failed(f, retryFor(next, f)), loaded: [], flow: idle }));
          return done(progress(next, fx));
        }
        const pages = m.o.value;
        const prs = mergeHits(pages.map((p) => p.nodes));
        const lost = unloadedOf(pages);
        let next = s;
        if (lost?.rateLimited) next = armResume(next, fx, lost.cause instanceof RateLimitError ? lost.cause.limit.until : null);
        const unloaded = lost && { count: lost.count, failure: failureOf(lost.cause) };
        const cut = cutOf(pages, s.sections[m.si].attention ? DASH_ATTENTION_HITS : DASH_PAGE);
        const read = deps.now();
        // Its rows are read anew: none of them waits for a re-read by ID.
        const dirty = new Set(next.dirty);
        for (const p of prs) dirty.delete(p.id);
        next = publish({ ...next, dirty }, deps, m.si, prs, cut, unloaded, { fresh: true, saved: false });
        if (next.run && !flow.reload) next = { ...next, run: { ...next.run, found: withShown(next.run.found, prs) } };
        next = withSlot(next, m.si, { flow: { at: 'attention', fresh: flow.fresh, reload: flow.reload, read, prs, cut, unloaded } });
        next = startAttention(next, prs, flow.fresh, flow.reload ?? next.run ?? { tick: next.tick, ticket: begin() }, fx);
        return done(progress(next, fx));
      }
      case 'attention': {
        const attn = new Map(s.attn);
        for (const p of m.prs) attn.set(p.id, 'done');
        let next: DashState = { ...s, attn, attnLanes: s.attnLanes.filter((l) => l !== m.lane) };
        const result: AttentionResult = m.o.ok
          ? { ...m.o.value, rows: new Map(m.o.value.rows) }
          : {
              rows: new Map(m.prs.map((p) => [p.id, { members: null, gap: { part: 'engine', failure: failureOf(m.o.ok ? null : m.o.error) } }])),
              checkAgain: false,
              checksOk: false,
              limitedUntil: null,
            };
        // An answer whose reads began before its pull request changed is refused: the row is read again.
        const dirty = new Set(next.dirty);
        const refused: string[] = [];
        for (const p of m.prs) {
          if ((next.changes.get(keyOf(p)) ?? -1) >= m.began.tick) {
            result.rows.delete(p.id);
            refused.push(p.id);
          } else dirty.delete(p.id);
        }
        next = withPool({ ...next, dirty, known: applyAttention(next.known, result, m.began.ticket) });
        if (refused.length) next = markDirty(next, refused, false, fx);
        if (result.checkAgain) next = armCheckAgain(next, fx);
        else if (result.checksOk) next = { ...next, checkFailures: 0 };
        if (result.limitedUntil !== null) next = armResume(next, fx, result.limitedUntil);
        return done(progress(next, fx));
      }
      case 'changed': {
        const ids = [...loadedRows(s).values()].filter((p) => keyOf(p) === m.key).map((p) => p.id);
        // Work that began reading before now (its tick is at most this one) learns nothing of the pull request that holds.
        const next = { ...s, tick: s.tick + 1, changes: new Map(s.changes).set(m.key, s.tick) };
        return done(markDirty(next, ids, false, fx));
      }
      case 'recheck':
        return done(maybeRecheck({ ...s, recheckDue: s.dirty.size > 0 }, fx));
      case 'rechecked': {
        const recheck = s.recheck;
        if (recheck?.phase !== 'rows') return done(s);
        if (!m.o.ok) {
          // The rows stay held back; they are read again when GitHub may answer.
          const e = m.o.error;
          const next: DashState = { ...s, recheck: null };
          return done(e instanceof RateLimitError ? armResume(next, fx, e.limit.until) : armCheckAgain(next, fx));
        }
        const got = m.o.value;
        const fresh = new Map<string, DashPr>();
        const gone = new Set<string>();
        const unread: DashPr[] = [];
        m.prs.forEach((p, i) => {
          const r = got[i];
          if (r && r !== 'sso') fresh.set(p.id, r);
          else if (r === 'sso') unread.push(p);
          else gone.add(p.id); // GitHub no longer shows it to the viewer
        });
        const dirty = new Set(s.dirty);
        for (const p of unread) dirty.delete(p.id);
        for (const id of gone) dirty.delete(id);
        const errors = new Map(s.known.errors);
        for (const p of unread)
          errors.set(p.id, { part: 'github', failure: failureOf(new Error('Reading this pull request needs SAML SSO authorization')) });
        const attn = new Map(s.attn);
        for (const id of fresh.keys()) attn.delete(id);
        let next: DashState = {
          ...s,
          dirty,
          attn,
          known: { ...s.known, errors },
          slots: s.slots.map((slot) =>
            slot.loaded ? { ...slot, loaded: slot.loaded.filter((p) => !gone.has(p.id)).map((p) => fresh.get(p.id) ?? p) } : slot,
          ),
          recheck: { ...recheck, phase: 'attention', ids: new Set(fresh.keys()) },
        };
        next = republish(withPool(next), deps);
        next = startAttention(next, [...fresh.values()], false, recheck, fx);
        return done(progress(next, fx));
      }
      case 'gap': {
        if (!m.o.ok) return done(m.o.error instanceof RateLimitError ? armResume(s, fx, m.o.error.limit.until) : armCheckAgain(s, fx));
        const { inputs, failed: unchecked } = m.o.value;
        const loaded = loadedRows(s);
        // Changed since its inputs were read (or none kept to tell by): read again. A failed check is tried again later.
        const changed = m.prs.filter((p) => loaded.has(p.id) && !inputs.has(p.id) && !unchecked.has(p.id) && !s.known.errors.has(p.id));
        let next = s;
        if ([...unchecked.values()].some((e) => e instanceof RateLimitError)) next = armResume(next, fx);
        else if (unchecked.size) next = armCheckAgain(next, fx);
        return done(markDirty(next, changed.map((p) => p.id), true, fx));
      }
      case 'outside': {
        const tail = s.tail;
        if (tail?.phase !== 'outside') return done(s);
        return done(outsideRead(s, tail, m.o.ok ? m.o.value : null, fx));
      }
      case 'saved':
        return done(s.tail?.phase === 'saving' ? { ...s, tail: null } : s);
      case 'notice': {
        const { topic, notice } = m;
        if (!notice) {
          if (topic === 'open') return done(streamOpened(s, fx));
          if (mayRefetch(topic as Trigger, s)) return done(refetch(s, fx));
          return done(paused() ? armResume(s, fx) : s);
        }
        if (!ROW_NOTICES.has(notice.kind) || !topic.startsWith('repo:')) return done(s);
        const repo = topic.slice('repo:'.length).toLowerCase();
        const number = notice.number;
        const shown = s.shown.get(repo);
        if (!shown || number === undefined || !shown.has(number)) return done(s);
        // Recorded in the log: the kept inputs and the saved copy drop what they hold of it, here and in the other tabs.
        const kind = notice.kind;
        fx.push(task(`forget ${s.seq}`, () => deps.kept.forget(repo, number, { source: 'notice', kind }), () => ({ t: 'done' })));
        const next = { ...s, seq: s.seq + 1 };
        if (!mayRefetch('notice', s)) return done(armResume(next, fx));
        fx.push(after('notice', NOTICE_RELOAD_DELAY, { t: 'refetch' }));
        return done(next);
      }
      case 'done':
        return done(s);
      case 'resume-armed':
        if (s.resume !== 'arming') return done(s);
        return done({ ...s, resume: m.s, slots: s.slots.map((x) => ({ ...x, shown: adopt(x.shown, m.s) })) });
      case 'resume-lapsed':
        return done({ ...s, resume: s.resume === m.s ? null : s.resume, slots: s.slots.map((x) => ({ ...x, shown: settle(x.shown, m.s) })) });
      case 'resume-fire': {
        if (s.resume !== m.s) return done(s);
        const next: DashState = { ...s, resume: null };
        // A pause extended meanwhile is waited out too.
        if (paused()) return done(armResume(next, fx));
        return done(refetch(next, fx));
      }
      case 'check-again':
        return done(refetch({ ...s, checkArmed: false }, fx));
    }
  };
}
